import {
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	Typography,
	Button,
	Divider,
	Checkbox,
	List,
	ListItem,
	ListItemText,
	ListItemIcon,
	Stack,
} from "@mui/material";
import { IInstallmentProposal } from "../../../../../shared/services/api/proposal/ProposalService";
import { useState } from "react";
import { globalFormatCurrencyBRL } from "../../../../../shared/utils";
import { format } from "date-fns";

interface ModalSelectInstallmentsToCancelProps {
	installments: IInstallmentProposal[];
	onConfirm: (selectedIds: string[]) => void;
	onClose: () => void;
}

export const ModalSelectInstallmentsToCancel = ({ installments, onConfirm, onClose }: ModalSelectInstallmentsToCancelProps) => {
	const [selectedIds, setSelectedIds] = useState<string[]>([]);

	const sortedInstallments = [...installments].sort((a, b) => (a.number ?? 0) - (b.number ?? 0));

	const allSelected = selectedIds.length === sortedInstallments.length;
	const partiallySelected = selectedIds.length > 0 && !allSelected;

	const toggleSelection = (id: string) => {
		setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
	};

	const toggleSelectAll = () => {
		if (allSelected) {
			setSelectedIds([]);
		} else {
			setSelectedIds(sortedInstallments.map((item) => item.id));
		}
	};

	const handleConfirm = () => {
		if (selectedIds.length > 0) {
			onConfirm(selectedIds);
		}
	};

	return (
		<Dialog open onClose={onClose} maxWidth="sm" fullWidth>
			<DialogTitle
				sx={{
					display: "flex",
					justifyContent: "space-between",
					alignItems: "center",
					pb: 1,
				}}
			>
				<Typography variant="h6" fontWeight={400} color="primary.main">
					Selecione as parcelas para cancelar
				</Typography>
			</DialogTitle>

			<Divider />

			<DialogContent sx={{ px: 4, pt: 2, maxHeight: "60vh" }}>
				{sortedInstallments.length === 0 ? (
					<Typography variant="body2" color="text.secondary">
						Nenhuma parcela pendente encontrada.
					</Typography>
				) : (
					<List dense disablePadding>
						{/* Select All */}
						<ListItem divider>
							<ListItemIcon>
								<Checkbox edge="start" checked={allSelected} indeterminate={partiallySelected} onChange={toggleSelectAll} />
							</ListItemIcon>
							<ListItemText primary="Selecionar todas as parcelas pendentes" primaryTypographyProps={{ fontWeight: 500 }} />
						</ListItem>

						{/* Lista ordenada */}
						{sortedInstallments.map((item) => (
							<ListItem
								key={item.id}
								divider
								secondaryAction={
									<Typography variant="body2" fontWeight={500} color={item.typeAmount === "discount" ? "error.main" : undefined}>
										{item.typeAmount === "discount" ? `- ${globalFormatCurrencyBRL(item.amount)}` : globalFormatCurrencyBRL(item.amount)}
									</Typography>
								}
							>
								<ListItemIcon>
									<Checkbox edge="start" checked={selectedIds.includes(item.id)} onChange={() => toggleSelection(item.id)} />
								</ListItemIcon>
								<ListItemText
									primary={item.typeAmount === "discount" ? "Desconto" : item.typeAmount === "add" ? "Acréscimo" : `Parcela ${item.number}`}
									secondary={`Vencimento: ${format(new Date(item.dueDate + " 03:00:00"), "dd/MM/yyyy")}`}
								/>
							</ListItem>
						))}
					</List>
				)}
			</DialogContent>

			<DialogActions sx={{ px: 4, pb: 3, pt: 1 }}>
				<Stack direction="row" justifyContent="space-between" width="100%">
					<Button variant="outlined" onClick={onClose}>
						Fechar
					</Button>
					<Button variant="contained" color="primary" disabled={selectedIds.length === 0} onClick={handleConfirm}>
						Continuar
					</Button>
				</Stack>
			</DialogActions>
		</Dialog>
	);
};
